import { useCallback, useEffect, useState } from "react";
import { useInfiniteQuery } from "@tanstack/react-query";
import { useInView } from "react-intersection-observer";
import DatePicker from "react-datepicker";
import Select from "react-select";
import debounce from "lodash.debounce";

import { fetchFinanceInvoices } from "../../utils/apiUtils";
import SharedTable from "../benefitPlan/SharedTable";

// Finance audit ledger — one row per PBM invoice payable the PBM Invoice agent
// successfully created in HRP. Backed by the FinanceInboundInvoices DynamoDB
// table (see AgentsStack). Used both in the Finance section and the DynamoDB
// browser, so it stays self-contained.

const filterOptions = [
    { value: "pbm", label: "PBM" },
    { value: "supplierHccId", label: "Supplier HCC id" },
    { value: "submittedByUserId", label: "Submitted by" },
    { value: "payableIdentifier", label: "Payable identifier" },
];

const toIsoDate = (date) => {
    if (!date) return undefined;
    const d = new Date(date);
    const tzOffsetMs = d.getTimezoneOffset() * 60000;
    return new Date(d.getTime() - tzOffsetMs).toISOString().slice(0, 10);
};

const FinanceInboundInvoices = () => {
    const [filterField, setFilterField] = useState(null);
    const [filterValue, setFilterValue] = useState("");
    const [debouncedFilterValue, setDebouncedFilterValue] = useState("");
    const [fromDate, setFromDate] = useState(null);
    const [toDate, setToDate] = useState(null);
    const [showMoreColumns, setShowMoreColumns] = useState(false);

    const { ref, inView } = useInView();

    const from = toIsoDate(fromDate);
    const to = toIsoDate(toDate);

    const queryKey = [
        "finance-inbound-invoices",
        filterField?.value,
        debouncedFilterValue,
        from,
        to,
    ];

    const {
        data,
        fetchNextPage,
        isLoading,
        isFetching,
        hasNextPage,
        isFetchingNextPage,
        status,
        error,
    } = useInfiniteQuery({
        retry: false,
        refetchOnWindowFocus: true,
        queryKey,
        queryFn: ({ pageParam }) => {
            const params = { lastEvaluatedKey: pageParam, from, to };
            if (filterField?.value && debouncedFilterValue) {
                params[filterField.value] = debouncedFilterValue;
            }
            return fetchFinanceInvoices(params);
        },
        select: (data) => ({
            pages: data.pages.flatMap((page) => page.Items || []),
            pageParams: data.pageParams,
        }),
        getNextPageParam: (lastPage) =>
            lastPage.LastEvaluatedKey
                ? encodeURIComponent(JSON.stringify(lastPage.LastEvaluatedKey))
                : null,
        staleTime: 0,
        enabled: true,
    });

    useEffect(() => {
        if (inView && hasNextPage) fetchNextPage();
    }, [inView, hasNextPage, fetchNextPage]);

    const debouncedChangeHandler = useCallback(
        debounce((value) => setDebouncedFilterValue(value?.trim()), 500),
        []
    );

    const items = data?.pages?.flat();

    let columns = [
        "submittedAt",
        "pbm",
        "invoiceNumber",
        "supplierName",
        "supplierHccId",
        "amount",
        "accountHccId",
        "submittedByUserId",
        "payableIdentifier",
        "status",
    ];

    if (showMoreColumns) {
        columns = [
            ...columns,
            "groupName",
            "taxId",
            "totalDue",
            "adminFeeExcluded",
            "paymentCycleIdentifier",
            "bankAccountName",
            "releaseDate",
            "invoiceDate",
            "servicePeriodStart",
            "servicePeriodEnd",
            "invoiceFileName",
            "invoiceS3Key",
            "description",
        ];
    }

    const clearFilters = () => {
        setFilterField(null);
        setFilterValue("");
        setDebouncedFilterValue("");
        setFromDate(null);
        setToDate(null);
    };

    return (
        <div className="container">
            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "start" }}>
                <div>
                    <p className="title">Finance Inbound Invoices</p>
                    <p className="sub-title">
                        PBM invoice payables created in HRP. Filter by date range, PBM, supplier, or submitter.
                    </p>
                </div>
            </div>

            <div style={{ display: "flex", gap: "1rem", alignItems: "flex-end", flexWrap: "wrap", padding: "1rem 1rem 1rem 0rem" }}>
                <div style={{ display: "flex", flexDirection: "column", gap: ".25rem", minWidth: "200px" }}>
                    <label style={{ fontSize: ".75rem", color: "var(--grey)" }}>From</label>
                    <DatePicker
                        selected={fromDate}
                        onChange={(date) => setFromDate(date)}
                        dateFormat="yyyy-MM-dd"
                        placeholderText="Start date"
                        isClearable
                        className="date-picker"
                        wrapperClassName="date-picker-wrapper"
                    />
                </div>
                <div style={{ display: "flex", flexDirection: "column", gap: ".25rem", minWidth: "200px" }}>
                    <label style={{ fontSize: ".75rem", color: "var(--grey)" }}>To</label>
                    <DatePicker
                        selected={toDate}
                        onChange={(date) => setToDate(date)}
                        dateFormat="yyyy-MM-dd"
                        placeholderText="End date"
                        isClearable
                        className="date-picker"
                        wrapperClassName="date-picker-wrapper"
                    />
                </div>

                <div style={{ display: "flex", flexDirection: "column", gap: ".25rem", minWidth: "200px" }}>
                    <label style={{ fontSize: ".75rem", color: "var(--grey)" }}>Filter by</label>
                    <Select
                        name="financeInvoiceFilter"
                        className="react-select-container"
                        classNamePrefix="react-select"
                        options={filterOptions}
                        value={filterField}
                        isClearable
                        onChange={(option) => {
                            setFilterField(option);
                            setFilterValue("");
                            setDebouncedFilterValue("");
                        }}
                    />
                </div>

                {filterField?.value && (
                    <div style={{ display: "flex", flexDirection: "column", gap: ".25rem" }}>
                        <label style={{ fontSize: ".75rem", color: "var(--grey)" }}>{filterField.label}</label>
                        <input
                            style={{ fontSize: "1rem", minWidth: "220px", padding: ".9rem", borderRadius: ".4rem", border: "1px solid var(--lighterGrey)", outline: "none" }}
                            type="text"
                            placeholder={`Search ${filterField.label}`}
                            autoComplete="off"
                            value={filterValue}
                            onChange={(e) => {
                                setFilterValue(e.target.value);
                                debouncedChangeHandler(e.target.value);
                            }}
                        />
                    </div>
                )}
            </div>

            <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", padding: "0rem 1rem 1rem 0rem" }}>
                <p style={{ cursor: "pointer", color: "var(--red)" }} onClick={clearFilters}>
                    Clear filters
                </p>
                <p style={{ cursor: "pointer", color: "var(--green)" }} onClick={() => setShowMoreColumns(!showMoreColumns)}>
                    {showMoreColumns ? "Less Columns" : "More Columns"}
                </p>
            </div>

            {status === "error" ? (
                <span className="danger">Error: {error.message}</span>
            ) : (
                <SharedTable
                    type={"finance-inbound-invoices"}
                    columns={columns}
                    items={items}
                    activeDraftId={null}
                    hasNextPage={hasNextPage}
                    isFetchingNextPage={isFetchingNextPage}
                    fetchNextPage={fetchNextPage}
                    isFetching={isFetching}
                    isLoading={isLoading}
                    noResultsMessage="No invoice submissions yet. They appear here once the PBM Invoice agent creates a payable in HRP."
                    ref={ref}
                />
            )}
        </div>
    );
};

export default FinanceInboundInvoices;
